/**
 * Language selector module – accessible disclosure dropdown.
 *
 * Markup contract:
 *   .lang-selector
 *     button.lang-selector__trigger[aria-expanded][aria-controls="lang-panel"]
 *     div#lang-panel.lang-selector__panel[role="dialog"][aria-label="Language"]
 *       button.lang-selector__option[aria-pressed="true|false"][hreflang="en"]
 *         span (label text)
 *         span.lang-selector__radio
 *
 * Keyboard behaviour:
 *   Trigger — Enter/Space  open panel, focus first option
 *   Trigger — Escape       close panel
 *   Panel   — Arrow Up/Down  move between options
 *   Panel   — Escape         close panel, return focus to trigger
 *   Panel   — Tab            close panel, move focus naturally
 *   Click outside            close panel
 *
 * @module lang-selector
 */

const langSelectorRegistry = [];
let hasDocumentClickListener = false;

/**
 * Returns all option buttons inside a panel.
 *
 * @param {HTMLElement} panel - The .lang-selector__panel element
 * @returns {HTMLButtonElement[]}
 */
function getOptions(panel) {
  return Array.from(panel.querySelectorAll('.lang-selector__option'));
}

/**
 * Opens the language panel and moves focus to the currently active option,
 * or to the first option if none is active.
 *
 * @param {HTMLButtonElement} trigger - The globe trigger button
 * @param {HTMLElement}       panel   - The .lang-selector__panel element
 * @returns {void}
 */
function openPanel(trigger, panel) {
  trigger.setAttribute('aria-expanded', 'true');
  panel.classList.add('is-open');

  const options = getOptions(panel);
  const active = options.find((o) => o.getAttribute('aria-pressed') === 'true') || options[0];
  if (active) {
    active.focus();
  }
}

/**
 * Closes the language panel.
 *
 * @param {HTMLButtonElement} trigger     - The globe trigger button
 * @param {HTMLElement}       panel       - The .lang-selector__panel element
 * @param {boolean}           [returnFocus=false] - When true, returns focus to the trigger
 * @returns {void}
 */
function closePanel(trigger, panel, returnFocus = false) {
  trigger.setAttribute('aria-expanded', 'false');
  panel.classList.remove('is-open');
  if (returnFocus) {
    trigger.focus();
  }
}

/**
 * Initialises all `.lang-selector` elements found in the document.
 *
 * @returns {void}
 */
function initLangSelector() {
  const selectors = document.querySelectorAll('.lang-selector');
  if (!selectors.length) return;

  selectors.forEach((root) => {
    if (root.dataset.langSelectorInitialized === 'true') return;

    const trigger = root.querySelector('.lang-selector__trigger');
    const panelId = trigger && trigger.getAttribute('aria-controls');
    const panel = panelId ? document.getElementById(panelId) : root.querySelector('.lang-selector__panel');

    if (!trigger || !panel) return;

    root.dataset.langSelectorInitialized = 'true';
    langSelectorRegistry.push({ root, trigger, panel });

    // ── Trigger ─────────────────────────────────────────────────────────────
    trigger.addEventListener('click', () => {
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        closePanel(trigger, panel, false);
      } else {
        openPanel(trigger, panel);
      }
    });

    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && trigger.getAttribute('aria-expanded') === 'true') {
        e.preventDefault();
        closePanel(trigger, panel, true);
      }
    });

    // ── Panel keyboard navigation ────────────────────────────────────────────
    panel.addEventListener('keydown', (e) => {
      const options = getOptions(panel);
      const currentIndex = options.indexOf(document.activeElement);

      switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const next = (currentIndex + 1) % options.length;
        options[next].focus();
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prev = (currentIndex - 1 + options.length) % options.length;
        options[prev].focus();
        break;
      }
      case 'Escape':
        e.preventDefault();
        closePanel(trigger, panel, true);
        break;
      case 'Tab':
        closePanel(trigger, panel, false);
        break;
      default:
        break;
      }
    });
    // ── Focus leaves the component ───────────────────────────────────────────
    root.addEventListener('focusout', (e) => {
      if (!root.contains(e.relatedTarget)) {
        closePanel(trigger, panel, false);
      }
    });
  });

  if (hasDocumentClickListener) return;

  document.addEventListener('click', (e) => {
    langSelectorRegistry.forEach(({ root, trigger, panel }) => {
      if (!root.contains(e.target)) {
        closePanel(trigger, panel, false);
      }
    });
  });

  hasDocumentClickListener = true;
}

export { initLangSelector };
